import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Divider,
  Autocomplete,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import TableCompo from '../Table/TableCompo';
import { CiEdit } from 'react-icons/ci';
import {
  getDistinctFollowupTitlesRequest,
  getFollowUpsByDateRequest,
  getFollowUpByIdRequest,
  getFollowUpCountsRequest,
  clearFollowUpById,
  createFollowUpRequest,
  updateFollowUpRequest,
} from '../../../redux/FollowUp/followupSlice';
import { fetchInstalaxmiAdminRequest } from '../../../redux/Tickets/ticketSlice';
import { updateLeadRequest } from '../../../redux/Required/requiredSlice';
import useCsvConverter from '../../../hooks/convertToCSV';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const STATUS_COLOR = {
  created: '#6B7280',
  'in-progress': '#D97706',
  completed: '#059669',
  hold: '#2563EB',
  closed: '#DC2626',
};

const STATUS_META = {
  created: { label: 'Created', color: '#6B7280', bg: '#F3F4F6' },
  'in-progress': { label: 'In Progress', color: '#B45309', bg: '#FEF3C7' },
  completed: { label: 'Completed', color: '#047857', bg: '#D1FAE5' },
  hold: { label: 'Hold', color: '#1D4ED8', bg: '#DBEAFE' },
  closed: { label: 'Closed', color: '#B91C1C', bg: '#FEE2E2' },
};

const TITLE_SHORT = {
  'Intent Amount': 'Intent',
  'Documents Received': 'Documents',
  'Advance Amount Received': 'Advance',
  'Payment gateway Created': 'Payment GW',
  'Android App Created': 'Android',
  'iOS App Created': 'iOS',
  'In-store branding delivered': 'Branding',
  'Training Scheduled': 'Trn. Scheduled',
  'Training Completed': 'Trn. Completed',
  'Full Payment received': 'Full Payment',
  'Dashboard Filled': 'Dashboard',
  'Start Date': 'Start Date',
  'Renewal Date': 'Renewal',
};

const getShortTitle = (title) => {
  if (TITLE_SHORT[title]) return TITLE_SHORT[title];
  if (!title) return 'Untitled';
  return title.length > 14 ? `${title.slice(0, 12)}…` : title;
};

const resolveCreatedBy = (item) => {
  const createdBy = item?.createdBy;
  const createdByUser = item?.createdByUser;
  const fromCreatedBy = Array.isArray(createdBy) ? createdBy[0] : createdBy;
  const fromCreatedByUser = Array.isArray(createdByUser)
    ? createdByUser[0]
    : createdByUser;
  const source =
    (fromCreatedBy && typeof fromCreatedBy === 'object' && fromCreatedBy) ||
    (fromCreatedByUser && typeof fromCreatedByUser === 'object' && fromCreatedByUser) ||
    {};

  return {
    userId:
      source._id || (typeof createdBy === 'string' ? createdBy : undefined),
    userName:
      source.name ||
      source.fullName ||
      source.userName ||
      item?.userName ||
      item?.name ||
      '-',
  };
};

const formatFollowupNotes = (notes) => {
  const items = Array.isArray(notes) ? [...notes] : notes ? [notes] : [];
  return items
    .sort((a, b) => (Number(b?.createdAt) || 0) - (Number(a?.createdAt) || 0))
    .map((item) => {
      if (typeof item === 'string') {
        return { text: item, time: '', userName: '-' };
      }
      const { userId, userName } = resolveCreatedBy(item);
      return {
        text: item?.note || '',
        time: item?.createdAt
          ? new Date(item.createdAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
          : '',
        userId,
        userName,
      };
    })
    .filter((item) => item.text);
};

const commentAsText = (comment) => {
  if (!comment) return '';
  if (typeof comment === 'string') return comment;
  if (Array.isArray(comment)) {
    const first = comment[0];
    return typeof first === 'string' ? first : first?.comment || '';
  }
  return comment.comment || '';
};

const normalizeTitleOptions = (rawTitles) => {
  if (!Array.isArray(rawTitles)) return [];
  return rawTitles
    .map((item) =>
      typeof item === 'string' ? item : item?.title || item?.name || '',
    )
    .map((title) => String(title).trim())
    .filter(Boolean);
};

const mergeUniqueTitles = (...lists) => {
  const seen = new Set();
  const result = [];
  lists.flat().forEach((item) => {
    const title = String(item || '').trim();
    if (!title) return;
    const key = title.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(title);
  });
  return result;
};

const TitleMultiSelect = ({ label, value, onChange, options, placeholder }) => (
  <Autocomplete
    multiple
    size="small"
    options={options}
    value={value}
    onChange={(event, newValue) => onChange(newValue)}
    limitTags={2}
    renderTags={(selected, getTagProps) =>
      selected.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option}
          size="small"
          label={option}
          sx={{ fontWeight: 600 }}
        />
      ))
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={value.length ? '' : placeholder}
      />
    )}
    sx={{
      minWidth: 220,
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
      },
    }}
  />
);

const TITLE_OPTIONS = [
  'Intent Amount',
  'Documents Received',
  'Advance Amount Received',
  'Payment gateway Created',
  'Android App Created',
  'iOS App Created',
  'In-store branding delivered',
  'Training Scheduled',
  'Training Completed',
  'Full Payment received',
];

const StatusLegend = () => (
  <Stack
    direction="row"
    spacing={1.5}
    flexWrap="wrap"
    useFlexGap
    sx={{ alignItems: 'center' }}
  >
    <Typography
      sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', mr: 0.5 }}
    >
      Status:
    </Typography>
    {Object.entries(STATUS_META).map(([key, meta]) => (
      <Stack
        key={key}
        direction="row"
        spacing={0.6}
        alignItems="center"
        sx={{
          px: 1,
          py: 0.35,
          borderRadius: '6px',
          backgroundColor: meta.bg,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: meta.color,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: meta.color }}>
          {meta.label}
        </Typography>
      </Stack>
    ))}
    <Stack
      direction="row"
      spacing={0.6}
      alignItems="center"
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: '6px',
        backgroundColor: '#F9FAFB',
        border: '1px dashed #D1D5DB',
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF' }}>
        + Not started — click to create
      </Typography>
    </Stack>
  </Stack>
);

const SummaryCard = ({
  label,
  value,
  subtitle,
  icon,
  iconBg,
  iconColor,
}) => (
  <Box
    sx={{
      flex: '1 1 0',
      minWidth: 150,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 2,
      py: 1.75,
      borderRadius: '12px',
      backgroundColor: '#fff',
      border: '1px solid #E5E7EB',
      boxShadow: 'none',
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '10px',
        backgroundColor: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: iconColor,
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, pt: 0.1 }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: '#374151',
          lineHeight: 1.3,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: '#111827',
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: '#9CA3AF',
            lineHeight: 1.3,
            mt: 0.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const formatCreatedOn = (value) => {
  if (!value) return null;
  const date = new Date(typeof value === 'number' ? value : value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const FollowUpListByDate = ({
  followUpType = 'allFollowUp',
  presetStartDate = null,
  presetEndDate = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { convertToCsv } = useCsvConverter();
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState('');

  // Dialog state: 'view' | 'confirm-create' | 'create-form'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState(null);
  const [pendingLead, setPendingLead] = useState(null);
  const [pendingTitle, setPendingTitle] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Create-form fields
  const [formTitle, setFormTitle] = useState('');
  const [formStatus, setFormStatus] = useState('created');
  const [formComment, setFormComment] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formDate, setFormDate] = useState(null);
  const [formRM, setFormRM] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [exporting, setExporting] = useState(false);
  const [completedTitles, setCompletedTitles] = useState([]);
  const [inProgressTitles, setInProgressTitles] = useState([]);
  const [pendingTitles, setPendingTitles] = useState([]);
  const [startDate, setStartDate] = useState(
    presetStartDate ? dayjs(presetStartDate) : null,
  );
  const [endDate, setEndDate] = useState(
    presetEndDate ? dayjs(presetEndDate) : null,
  );
  const [openConversionDialog, setOpenConversionDialog] = useState(false);
  const [selectedLeadForConversion, setSelectedLeadForConversion] =
    useState(null);

  const {
    followUp,
    distinctTitle,
    total = 0,
    loading,
    error,
    followUpById,
    followUpByIdLoading,
    followUpByIdError,
    followUpCounts,
  } = useSelector((state) => state.followUp || {});

  const { instalaxmiAdmin } = useSelector((state) => state.tickets || {});
  const assignToList = Array.isArray(instalaxmiAdmin)
    ? instalaxmiAdmin
    : Array.isArray(instalaxmiAdmin?.data)
      ? instalaxmiAdmin.data
      : [];

  const package_name = useSelector(
    (state) => state?.appConfig?.configData?.package_name || '',
  );

  const leads = Array.isArray(followUp) ? followUp : [];
  const titlesFromApi = normalizeTitleOptions(distinctTitle);
  const titlesFromRows = leads.flatMap((lead) =>
    (Array.isArray(lead?.followUps) ? lead.followUps : []).map(
      (item) => item?.title,
    ),
  );
  const titles =
    followUpType === 'myFollowUps'
      ? titlesFromApi.length > 0
        ? titlesFromApi
        : mergeUniqueTitles(titlesFromRows)
      : TITLE_OPTIONS;
  const visibleTitles = titles;
  const filterTitleOptions = titles;

  const stats = followUpCounts || {
    total: 0,
    inProgress: 0,
    completed: 0,
    hold: 0,
    closed: 0,
    created: 0,
  };

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const jewellerID = storedUser?.jeweller;

  useEffect(() => {
    dispatch(getDistinctFollowupTitlesRequest({ type: followUpType }));
    dispatch(fetchInstalaxmiAdminRequest(jewellerID));
    dispatch(getFollowUpCountsRequest());
  }, [dispatch, jewellerID, followUpType]);

  const fetchTrackerList = () => {
    const filters = [
      { status: 'Completed', title: completedTitles },
      { status: 'IN_PROGRESS', title: inProgressTitles },
      { status: 'Pending', title: pendingTitles },
    ].filter((f) => f.title.length > 0);

    const payload = {
      page: pagination.page,
      limit: pagination.limit,
      search,
      filters,
      type: followUpType,
    };
    if (startDate?.isValid?.()) payload.startDate = startDate.format('YYYY-MM-DD');
    if (endDate?.isValid?.()) payload.endDate = endDate.format('YYYY-MM-DD');

    dispatch(getFollowUpsByDateRequest(payload));
  };

  useEffect(() => {
    setStartDate(presetStartDate ? dayjs(presetStartDate) : null);
    setEndDate(presetEndDate ? dayjs(presetEndDate) : null);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [presetStartDate, presetEndDate, followUpType]);

  useEffect(() => {
    fetchTrackerList();
  }, [
    dispatch,
    pagination,
    search,
    completedTitles,
    inProgressTitles,
    pendingTitles,
    followUpType,
    startDate,
    endDate,
  ]);

  const handleTitleFilterChange = (setter) => (newValue) => {
    setter(newValue);
    setPagination((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  };

  // Detect create-request completion (loading true -> false) to close dialog + refresh
  const prevLoadingRef = useRef(false);
  const creatingRef = useRef(false);

  useEffect(() => {
    if (prevLoadingRef.current && !loading && creatingRef.current) {
      creatingRef.current = false;
      if (!error) {
        toast.success(
          isEditMode
            ? 'Follow-up updated successfully!'
            : 'Follow-up created successfully!',
        );
        setDialogOpen(false);
        setDialogMode(null);
        resetCreateForm();
        fetchTrackerList();
        dispatch(getFollowUpCountsRequest());
      } else {
        toast.error(
          isEditMode
            ? 'Failed to update follow-up.'
            : 'Failed to create follow-up.',
        );
      }
    }
    prevLoadingRef.current = loading;
  }, [loading, error]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;
      case 'changeRowsPerPage':
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;
      default:
        break;
    }
  };

  const getLeadConversion = (row) =>
    String(row?.leadConversion || 'NO').toUpperCase() === 'YES' ? 'YES' : 'NO';

  const handleOpenConversionDialog = (row) => {
    setSelectedLeadForConversion(row);
    setOpenConversionDialog(true);
  };

  const handleCloseConversionDialog = () => {
    setOpenConversionDialog(false);
    setSelectedLeadForConversion(null);
  };

  const handleConfirmConversion = () => {
    if (!selectedLeadForConversion?._id) return;
    const nextStatus =
      getLeadConversion(selectedLeadForConversion) === 'YES' ? 'NO' : 'YES';
    dispatch(
      updateLeadRequest({
        id: selectedLeadForConversion._id,
        leadConversion: nextStatus,
      }),
    );
    handleCloseConversionDialog();
    if (nextStatus === 'YES' && !selectedLeadForConversion.jeweller) {
      navigate('/CreateJwellerList', {
        state: {
          openJewellerForm: true,
          lead: {
            ...selectedLeadForConversion,
            phone:
              selectedLeadForConversion.phone ||
              selectedLeadForConversion.contactNumber,
          },
        },
      });
      return;
    }
    toast.success(`Lead conversion updated to ${nextStatus}`);
    fetchTrackerList();
  };

  const handleCircleClick = (lead, title, matched) => {
    if (matched) {
      setDialogMode('view');
      setDialogOpen(true);
      dispatch(getFollowUpByIdRequest(matched._id));
    } else {
      setPendingLead(lead);
      setPendingTitle(title);
      setDialogMode('confirm-create');
      setDialogOpen(true);
    }
  };

  const resetCreateForm = () => {
    setFormTitle('');
    setFormStatus('created');
    setFormComment('');
    setFormNotes('');
    setFormDate(null);
    setFormRM('');
    setFormErrors({});
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogMode(null);
    setPendingLead(null);
    setPendingTitle(null);
    resetCreateForm();
    dispatch(clearFollowUpById());
  };

  // "Yes" on confirm-create -> switch to the create-form dialog, prefilled
  const handleConfirmCreate = () => {
    if (!pendingLead || !pendingTitle) return;
    setFormTitle(pendingTitle);
    setFormStatus('created');
    setDialogMode('create-form');
  };

  const handleEditFromView = () => {
    if (!followUpById) return;

    setIsEditMode(true);
    setEditingId(followUpById._id);
    setFormTitle(followUpById.title || '');
    setFormStatus(followUpById.status || 'created');
    setFormComment(commentAsText(followUpById.comment));
    setFormNotes('');
    setFormDate(
      followUpById.followupDate
        ? dayjs(Number(followUpById.followupDate))
        : null,
    );
    setFormRM(followUpById.assignTo?._id || '');
    setDialogMode('create-form');
  };

  const validateCreateForm = () => {
    const tempErrors = {};
    if (!formTitle?.trim()) tempErrors.formTitle = 'Please enter a title';
    if (!formRM) tempErrors.formRM = 'Please select RM';
    if (!formDate) tempErrors.formDate = 'Please select a date';
    if (!formStatus) tempErrors.formStatus = 'Please select a status';

    if (Object.keys(tempErrors).length > 0) {
      Object.values(tempErrors).forEach((msg) => toast.error(msg));
    }
    setFormErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSaveCreateForm = () => {
    if (!validateCreateForm()) return;

    creatingRef.current = true;

    if (isEditMode && editingId) {
      const updateData = {
        status: formStatus,
        followupDate: formDate?.toISOString(),
      };
      const trimmedComment = formComment.trim();
      if (trimmedComment) {
        updateData.comment = trimmedComment;
      }
      const trimmedNotes = formNotes.trim();
      if (trimmedNotes) {
        updateData.Notes = trimmedNotes;
      }
      dispatch(
        updateFollowUpRequest({
          id: editingId,
          data: updateData,
        }),
      );
    } else {
      if (!pendingLead?._id) return;
      const createPayload = {
        leadID: pendingLead._id,
        assignTo: formRM,
        status: formStatus,
        followupDate: formDate?.toISOString(),
        title: formTitle,
      };
      const trimmedComment = formComment.trim();
      if (trimmedComment) {
        createPayload.comment = trimmedComment;
      }
      const trimmedNotes = formNotes.trim();
      if (trimmedNotes) {
        createPayload.Notes = trimmedNotes;
      }
      dispatch(createFollowUpRequest(createPayload));
    }
  };

  const renderProgressChip = (row, title, matched) => {
    const meta = matched
      ? STATUS_META[matched.status] || {
          label: matched.status,
          color: '#6B7280',
          bg: '#F3F4F6',
        }
      : null;
    const short = getShortTitle(title);

    return (
      <Tooltip
        key={title}
        arrow
        title={
          matched
            ? `${title} — ${meta.label}`
            : `${title} — not started (click to create)`
        }
      >
        <Box
          onClick={() => handleCircleClick(row, title, matched)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCircleClick(row, title, matched);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.45,
            borderRadius: '8px',
            cursor: 'pointer',
            userSelect: 'none',
            border: matched
              ? `1px solid ${meta.color}33`
              : '1px dashed #D1D5DB',
            backgroundColor: matched ? meta.bg : '#FAFAFA',
            transition:
              'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderColor: matched ? meta.color : '#7028DA',
              backgroundColor: matched ? meta.bg : '#F5F3FF',
            },
          }}
        >
          {matched ? (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: meta.color,
                flexShrink: 0,
              }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: '#9CA3AF',
                lineHeight: 1,
                width: 10,
                textAlign: 'center',
              }}
            >
              +
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: matched ? '#111827' : '#6B7280',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {short}
          </Typography>
          {matched && (
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: meta.color,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {meta.label}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: 'jewellerName',
      headerName: 'Jeweller / Lead',
      width: 200,
      render: (val, row) => {
        const name = row.jewellerName || 'N.A';
        const initial = name !== 'N.A' ? name.charAt(0).toUpperCase() : '?';
        const createdOn = formatCreatedOn(row.createdAt);

        return (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ py: 0.5 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: '#F3E8FF',
                color: '#7A3FFF',
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#111827',
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {name}
              </Typography>
              {createdOn && (
                <Typography
                  sx={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.3 }}
                >
                  Created on {createdOn}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'contact',
      headerName: 'Contact',
      width: 130,
      render: (val, row) => {
        const address = [row.city, row.state].filter(Boolean).join(', ');
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              py: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: '#111827',
                lineHeight: 1.3,
              }}
            >
              {row.contactName || 'N.A'}
            </Typography>
            <Typography
              sx={{ fontSize: 12, color: '#4B5563', lineHeight: 1.3 }}
            >
              {row.contactNumber || 'N.A'}
            </Typography>
            {address && (
              <Typography
                sx={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.3 }}
              >
                {address}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'leadTojeweller',
      headerName: 'Jeweller App Status',
      width: 60,
      render: (val, row) => {
        const converted =
          String(val ?? row.jewellerConverted ?? '').toLowerCase() === 'yes';
          console.log(row.jewellerConverted);
        return (
          <Chip
            size="small"
            label={converted ? 'Existed' : 'Not Existed'}
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: 11,
              color: converted ? '#047857' : '#6B7280',
              backgroundColor: converted ? '#D1FAE5' : '#F3F4F6',
            }}
          />
        );
      },
    },
    {
      field: 'leadConversion',
      headerName: 'Lead Conversion',
      width: 60,
      render: (val, row) => {
        const current = getLeadConversion(row);
        const isYes = current === 'YES';
        return (
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenConversionDialog(row)}
            sx={{
              minWidth: 64,
              height: 26,
              px: 1.5,
              fontWeight: 700,
              fontSize: 11,
              textTransform: 'none',
              boxShadow: 'none',
              color: isYes ? '#047857' : '#6B7280',
              backgroundColor: isYes ? '#D1FAE5' : '#F3F4F6',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: isYes ? '#A7F3D0' : '#E5E7EB',
              },
            }}
          >
            {current}
          </Button>
        );
      },
    },
    {
      field: 'tracker',
      headerName: 'Follow-up Progress',
      flex: 1,
      minWidth: 480,
      render: (val, row) => {
        const rowFollowUps = Array.isArray(row.followUps) ? row.followUps : [];
        const rowTitles =
          followUpType === 'myFollowUps'
            ? mergeUniqueTitles(rowFollowUps.map((item) => item?.title))
            : visibleTitles;
        const doneCount = rowTitles.filter((title) =>
          rowFollowUps.some(
            (f) =>
              f.title?.toLowerCase() === title.toLowerCase() &&
              (f.status === 'completed' || f.status === 'closed'),
          ),
        ).length;
        const startedCount = rowTitles.filter((title) =>
          rowFollowUps.some(
            (f) => f.title?.toLowerCase() === title.toLowerCase(),
          ),
        ).length;

        if (followUpType === 'myFollowUps' && rowFollowUps.length === 0) {
          return (
            <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
              No follow-ups
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              py: 0.5,
              minWidth: 0,
              width: '100%',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={`${doneCount}/${rowTitles.length || 0} done`}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: 11,
                  color:
                    doneCount === rowTitles.length &&
                    rowTitles.length > 0
                      ? '#047857'
                      : '#5B21B6',
                  backgroundColor:
                    doneCount === rowTitles.length &&
                    rowTitles.length > 0
                      ? '#D1FAE5'
                      : '#F3E8FF',
                }}
              />
              <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
                {startedCount} started
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {rowTitles.map((title) => {
                const matched = rowFollowUps.find(
                  (f) => f.title?.toLowerCase() === title.toLowerCase(),
                );
                return renderProgressChip(row, title, matched);
              })}
            </Box>
          </Box>
        );
      },
    },
  ];

  if (loading && !dialogOpen && leads.length === 0) return <p>Loading...</p>;
  if (error && !dialogOpen) return <p>Error: {JSON.stringify(error)}</p>;

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadFollowUpCsv = () => {
    setExporting(true);

    const filters = [
      { status: 'Completed', title: completedTitles },
      { status: 'IN_PROGRESS', title: inProgressTitles },
      { status: 'Pending', title: pendingTitles },
    ].filter((f) => f.title.length > 0);

    const query = { type: followUpType };
    if (search) query.search = search;
    if (filters.length > 0) query.filters = JSON.stringify(filters);
    if (startDate?.isValid?.()) query.startDate = startDate.format('YYYY-MM-DD');
    if (endDate?.isValid?.()) query.endDate = endDate.format('YYYY-MM-DD');

    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/follow-up/followupsCsv', // matches @Get('followupsCsv') route
        query,
        callback: (response) => {
          setExporting(false);
          if (response?.success) {
            toast.success(response.message || 'Export completed.');
          } else {
            toast.error(response?.message || 'Export failed.');
          }
        },
      }),
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          p: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1.5,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={700}
              fontSize={24}
              fontFamily="Aeonik"
              sx={{ mb: 1.25 }}
            >
              {followUpType === 'myFollowUps'
                ? 'My Follow-ups'
                : 'Follow-up Tracker'}
            </Typography>
            {followUpType === 'allFollowUp' && <StatusLegend />}
          </Box>

          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '16px',
              background:
                'linear-gradient(145deg, #F5F3FF 0%, #EDE9FE 55%, #FCE7F3 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid #E9D5FF',
            }}
          >
            <AssignmentOutlinedIcon sx={{ fontSize: 36, color: '#7A3FFF' }} />
          </Box>
        </Box>

        {followUpType === 'allFollowUp' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(6, 1fr)',
            },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <SummaryCard
            label="Total Follow-ups"
            value={stats.total}
            subtitle="All time"
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 22 }} />}
            iconBg="#DBEAFE"
            iconColor="#2563EB"
          />
          <SummaryCard
            label="Created"
            value={stats.created}
            subtitle="Newly created"
            icon={<AddCircleOutlineIcon sx={{ fontSize: 22 }} />}
            iconBg="#F3F4F6"
            iconColor="#6B7280"
          />
          <SummaryCard
            label="In Progress"
            value={stats.inProgress}
            subtitle="Active follow-ups"
            icon={<ScheduleOutlinedIcon sx={{ fontSize: 22 }} />}
            iconBg="#FEF3C7"
            iconColor="#D97706"
          />
          <SummaryCard
            label="Completed"
            value={stats.completed}
            subtitle="Successfully done"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
            iconBg="#D1FAE5"
            iconColor="#059669"
          />
          <SummaryCard
            label="On Hold"
            value={stats.hold}
            subtitle="On hold"
            icon={<PauseCircleOutlineIcon sx={{ fontSize: 22 }} />}
            iconBg="#DBEAFE"
            iconColor="#2563EB"
          />
          <SummaryCard
            label="Closed"
            value={stats.closed}
            subtitle="Closed follow-ups"
            icon={<HighlightOffOutlinedIcon sx={{ fontSize: 22 }} />}
            iconBg="#FEE2E2"
            iconColor="#DC2626"
          />
        </Box>
        )}

        <CardContent sx={{ height: '100%', width: '100%', px: 0, pb: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 2,
            }}
          >
            <TitleMultiSelect
              label="Completed"
              value={completedTitles}
              onChange={handleTitleFilterChange(setCompletedTitles)}
              options={filterTitleOptions}
              placeholder="Select titles"
            />
            <TitleMultiSelect
              label="In Progress"
              value={inProgressTitles}
              onChange={handleTitleFilterChange(setInProgressTitles)}
              options={filterTitleOptions}
              placeholder="Select titles"
            />
            <TitleMultiSelect
              label="Pending"
              value={pendingTitles}
              onChange={handleTitleFilterChange(setPendingTitles)}
              options={filterTitleOptions}
              placeholder="Select titles"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
              mb: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(value) => {
                  setStartDate(value);
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 160 } },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                minDate={startDate || undefined}
                onChange={(value) => {
                  setEndDate(value);
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 160 } },
                }}
              />
            </LocalizationProvider>
            {(startDate || endDate) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{ textTransform: 'none', height: 40 }}
              >
                Clear Dates
              </Button>
            )}
          </Box>
          <TableCompo
            columns={
              followUpType === 'myFollowUps'
                ? columns.filter(
                    (col) =>
                      col.field !== 'leadTojeweller' &&
                      col.field !== 'leadConversion',
                  )
                : columns
            }
            data={JSON.parse(JSON.stringify(leads))}
            totalCount={total}
            stickyColumnFields={['jewellerName']}
            multiline
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadFollowUpCsv}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export Report'}
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`${process.env.REACT_APP_API_BASE_URL}/static_data/${package_name}/followup.xlsx?nocache=${Date.now()}`}
                  download="followup.xlsx"
                >
                  Download
                </Button>
              </Stack>
            }
          />
        </CardContent>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={dialogMode === 'create-form' ? 'sm' : 'xs'}
        fullWidth
      >
        {dialogMode === 'view' && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              Follow-up Details
              {followUpById && (
                <IconButton onClick={handleEditFromView} size="small">
                  <CiEdit sx={{ color: '#000' }} />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent dividers>
              {followUpByIdLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : followUpByIdError ? (
                <Typography color="error">
                  Failed to load follow-up details.
                </Typography>
              ) : followUpById ? (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#7028DA', fontWeight: 600 }}
                  >
                    {followUpById.title}
                  </Typography>
                  <Divider />
                  <Typography>
                    <b>Status:</b>{' '}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.3,
                        borderRadius: '6px',
                        color:
                          STATUS_META[followUpById.status]?.color || '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor:
                          STATUS_META[followUpById.status]?.bg ||
                          STATUS_COLOR[followUpById.status] ||
                          '#ada7a7',
                      }}
                    >
                      {STATUS_META[followUpById.status]?.label ||
                        followUpById.status}
                    </Box>
                  </Typography>
                  <Typography>
                    <b>Assigned To:</b> {followUpById.assignTo?.name || '—'}
                  </Typography>
                  <Typography>
                    <b>Created By:</b> {followUpById.createdBy?.name || '—'}
                  </Typography>
                  <Typography>
                    <b>Follow-up Date:</b>{' '}
                    {followUpById.followupDate
                      ? new Date(
                          Number(followUpById.followupDate),
                        ).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </Typography>
                  <Typography>
                    <b>Comment:</b> {commentAsText(followUpById.comment) || '—'}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                      Notes
                    </Typography>
                    {(() => {
                      const notes = formatFollowupNotes(followUpById.Notes);
                      if (!notes.length) {
                        return (
                          <Typography color="text.secondary">
                            No notes
                          </Typography>
                        );
                      }
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.25,
                          }}
                        >
                          {notes.map((item, idx) => (
                            <Box
                              key={item.userId ? `${item.userId}-${idx}` : idx}
                              sx={{
                                p: 1.5,
                                borderRadius: '10px',
                                border: '1px solid #E5E7EB',
                                background: '#F9FAFB',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: '#6B7280',
                                }}
                              >
                                {item.userName && item.userName !== '-'
                                  ? `${item.userName}${item.time ? ` • ${item.time}` : ''}`
                                  : item.time || '-'}
                              </Typography>
                              <Typography sx={{ mt: 0.5, fontSize: 14 }}>
                                {item.text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>

                  {Array.isArray(followUpById.statusHistory) &&
                    followUpById.statusHistory.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: '#333', mb: 1 }}
                        >
                          Status History
                        </Typography>
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{
                            maxHeight: 260,
                            borderRadius: '10px',
                            border: '1px solid #eee',
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    backgroundColor: '#faf7fe',
                                    color: '#5a2a9e',
                                  }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    backgroundColor: '#faf7fe',
                                    color: '#5a2a9e',
                                  }}
                                >
                                  Updated At
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    backgroundColor: '#faf7fe',
                                    color: '#5a2a9e',
                                  }}
                                >
                                  Updated By
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {[...followUpById.statusHistory]
                                .sort((a, b) => b.updatedAt - a.updatedAt)
                                .map((entry, idx) => (
                                  <TableRow
                                    key={`${entry.status}-${entry.updatedAt}-${idx}`}
                                    sx={{
                                      '&:last-child td': { border: 0 },
                                      '&:hover': {
                                        backgroundColor: '#fafafa',
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Chip
                                        label={
                                          STATUS_META[entry.status]?.label ||
                                          entry.status
                                        }
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          color:
                                            STATUS_META[entry.status]?.color ||
                                            '#fff',
                                          backgroundColor:
                                            STATUS_META[entry.status]?.bg ||
                                            STATUS_COLOR[entry.status] ||
                                            '#ada7a7',
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>
                                      {entry.updatedAt
                                        ? new Date(
                                            Number(entry.updatedAt),
                                          ).toLocaleString('en-IN', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })
                                        : '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>
                                      {entry.updatedBy?.name || '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                </Box>
              ) : (
                <Typography>No details found.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}

        {dialogMode === 'confirm-create' && (
          <>
            <DialogTitle>Create Follow-up?</DialogTitle>
            <DialogContent>
              <Typography>
                No <b>{pendingTitle}</b> follow-up exists for{' '}
                <b>{pendingLead?.jewellerName}</b>. Do you want to create it?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>No</Button>
              <Button
                variant="contained"
                sx={{
                  background:
                    'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
                }}
                onClick={handleConfirmCreate}
              >
                Yes
              </Button>
            </DialogActions>
          </>
        )}

        {dialogMode === 'create-form' && (
          <>
            <DialogTitle>
              {isEditMode ? 'Update Follow-up' : 'Create Follow-up'}
            </DialogTitle>
            <DialogContent>
              {!isEditMode && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  For <b>{pendingLead?.jewellerName}</b>
                </Typography>
              )}

              <Autocomplete
                disabled={isEditMode}
                sx={{ width: '100%', mt: 2 }}
                freeSolo
                options={TITLE_OPTIONS}
                size="small"
                value={formTitle}
                onChange={(e, newValue) => setFormTitle(newValue || '')}
                onInputChange={(e, newInputValue) =>
                  setFormTitle(newInputValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Title"
                    variant="outlined"
                    error={!!formErrors.formTitle}
                    helperText={formErrors.formTitle}
                  />
                )}
              />

              <FormControl
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                error={!!formErrors.formRM}
                disabled={isEditMode}
              >
                <InputLabel id="tracker-rm-label">
                  RM <span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  labelId="tracker-rm-label"
                  value={formRM}
                  label="RM"
                  onChange={(e) => setFormRM(e.target.value)}
                >
                  {assignToList.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.formRM && (
                  <Typography color="error" variant="caption">
                    {formErrors.formRM}
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Comment"
                placeholder="Add a comment (optional)"
                variant="outlined"
                multiline
                rows={3}
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                sx={{ mt: 2 }}
                disabled={isEditMode}
              />

              <TextField
                fullWidth
                label={isEditMode ? 'Add Note' : 'Notes'}
                placeholder="Add a note (optional)"
                variant="outlined"
                multiline
                rows={3}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Box sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disabled={isEditMode}
                    label="Follow-up Date"
                    value={formDate}
                    onChange={(newValue) => setFormDate(newValue)}
                    disablePast
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        error: !!formErrors.formDate,
                        helperText: formErrors.formDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel>Status ?</FormLabel>
                <RadioGroup
                  row
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                >
                  <FormControlLabel
                    value="created"
                    control={<Radio />}
                    label="Created"
                  />
                  <FormControlLabel
                    value="completed"
                    control={<Radio />}
                    label="Completed"
                  />
                  <FormControlLabel
                    value="in-progress"
                    control={<Radio />}
                    label="In-progress"
                  />
                  <FormControlLabel
                    value="hold"
                    control={<Radio />}
                    label="Hold"
                  />
                  <FormControlLabel
                    value="closed"
                    control={<Radio />}
                    label="Closed"
                  />
                </RadioGroup>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                variant="contained"
                sx={{
                  background:
                    'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
                }}
                onClick={handleSaveCreateForm}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={openConversionDialog}
        onClose={handleCloseConversionDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Lead Conversion</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to change status to{' '}
            <b>
              {getLeadConversion(selectedLeadForConversion) === 'YES'
                ? 'NO'
                : 'YES'}
            </b>
            {selectedLeadForConversion?.jewellerName
              ? ` for ${selectedLeadForConversion.jewellerName}`
              : ''}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={handleCloseConversionDialog}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
              WebkitBackgroundClip: 'text',
              fontWeight: '700',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmConversion}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowUpListByDate;
